/**
 * In-memory ring buffer of the API traffic this browser tab has made.
 *
 * Purpose: when a user reports "it didn't save", the answer is usually in the exact request
 * that failed — its status, the backend's code and message, and the `requestId` that pins the
 * matching server-side row. None of that survives a toast. This buffer keeps the last
 * {@link CAPACITY} calls so the Diagnostics drawer can show them and hand over a JSON dump the
 * user pastes into a ticket.
 *
 * Memory-only, by design: nothing is written to localStorage or sent anywhere. A reload clears
 * it. The durable record lives server-side (Admin → Errors) — this is the client half, and it
 * is the only half that can show a request that never reached the server at all.
 *
 * What is NOT recorded (the same rule as the server's access log): request and response BODIES
 * and headers. A login body carries a password and a response body can be a whole customer
 * list; neither belongs in a buffer users are encouraged to copy and paste. The failure detail
 * that matters — code, message, validation issues, requestId — comes from the error envelope,
 * which is already safe to display, since the backend wrote it to be shown.
 *
 * The store is a hand-rolled `useSyncExternalStore` source rather than a zustand store: the
 * axios interceptors that feed it are plain module code with no React in scope, and the
 * snapshot must stay referentially stable between pushes or every consumer re-renders on
 * every request.
 */

/** How many calls are retained. Beyond this the oldest entry falls off the end. */
export const CAPACITY = 200;

/** One recorded API call — a request that completed, failed, or never reached the server. */
export interface RequestLogEntry {
  /** Monotonic per-tab sequence number; also the React key. */
  id: number;
  /** ISO-8601 instant the request was issued. */
  at: string;
  method: string;
  /** Request URL as the client issued it (path + query), never a body. */
  url: string;
  /** HTTP status, or undefined when the request never produced a response. */
  status?: number;
  /** Round-trip duration in milliseconds. */
  durationMs: number;
  /** Correlation id from the response header / error envelope — the server-side lookup key. */
  requestId?: string;
  /** Backend error code, when the call failed with an envelope. */
  code?: string;
  /** Backend error message, when the call failed with an envelope. */
  message?: string;
  /** Structured error detail (validation issues, guard details), when present. */
  details?: Record<string, unknown>;
  /** True for anything that is not a 2xx — including a request that never got a response. */
  isError: boolean;
}

let entries: readonly RequestLogEntry[] = [];
let nextId = 1;
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) {
    listener();
  }
}

/** Append one call to the buffer, dropping the oldest once {@link CAPACITY} is reached. */
export function recordRequest(entry: Omit<RequestLogEntry, 'id'>): void {
  const next = [...entries, { ...entry, id: nextId }];
  nextId += 1;
  // Replace the array rather than mutating it: `useSyncExternalStore` compares snapshots by
  // reference, so an in-place push would render nothing.
  entries = next.length > CAPACITY ? next.slice(next.length - CAPACITY) : next;
  emit();
}

/** Subscribe to buffer changes (the `useSyncExternalStore` contract). */
export function subscribeToRequestLog(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** The current buffer, newest LAST. Referentially stable until the next record/clear. */
export function getRequestLog(): readonly RequestLogEntry[] {
  return entries;
}

/** Drop every recorded call — the "start clean before reproducing" action. */
export function clearRequestLog(): void {
  entries = [];
  emit();
}

/**
 * The buffer as a pretty-printed JSON document for the clipboard / a bug report. Entries are
 * emitted newest-first, which is the order a reader wants: the failure being reported is
 * almost always the last thing that happened.
 */
export function exportRequestLog(): string {
  return JSON.stringify(
    {
      exportedAt: new Date().toISOString(),
      userAgent: navigator.userAgent,
      entries: [...entries].reverse(),
    },
    null,
    2,
  );
}
